import React, { useCallback, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import {
	GRID_DETAIL_PANEL_TOGGLE_FIELD,
	type GridCellSelectionModel,
	type GridColDef,
	type GridRowId,
	type GridRowSelectionModel,
	type GridSortModel,
} from '@mui/x-data-grid-premium'
import { euiIconTypeMap } from '../../../components/icons'
import { LineItem } from '../../../types/demandRequest'
import { DemandRequestLineItemStatus } from '../../../graphql'
import {
	getRequestedItemsColumns,
	DEFAULT_SORT,
	comparePreferredVendor,
	PREFERRED_VENDOR_SORT_FIELD,
} from '../config/requestedItemsTableConfig'
import BulkActionsDropdown from './BulkActionsDropdown'
import SourcingArtifactTable from './SourcingArtifactTable'
import DataGrid from '../../components/DataGrid'
import { REQUESTS_TABLE_HEIGHT } from '../../helpers/utils'

const ArrowDownIcon = euiIconTypeMap.arrowDown
const ArrowRightIcon = euiIconTypeMap.arrowRight

// Cancelled line items read as struck-through history rather than actionable rows.
const CANCELLED_ROW_CLASS = 'RequestedItemsTable-cancelledRow'

// Listed in column order: copied cells come out in this order, whatever order they were clicked in.
const COPYABLE_FIELDS = new Set( [ 'quantityRequested', 'partNumber', 'manufacturer', 'description' ] )

const RequestsDataGrid = styled( DataGrid )( ( { theme } ) => ( {
	fontSize: '0.9375rem',
	'& .MuiDataGrid-columnHeaderTitleContainer': { whiteSpace: 'normal' },
	'& .MuiDataGrid-columnHeaderTitleContainerContent': { minWidth: 0 },
	'& .MuiDataGrid-columnHeaderTitle': {
		fontSize: '0.9375rem',
		fontWeight: 600,
		whiteSpace: 'normal',
		lineHeight: 1.25,
		display: '-webkit-box',
		WebkitBoxOrient: 'vertical',
		WebkitLineClamp: 2,
		overflow: 'hidden',
	},
	'& .MuiDataGrid-cell': {
		whiteSpace: 'normal',
		paddingTop: theme.spacing( 1.5 ),
		paddingBottom: theme.spacing( 1.5 ),
		alignContent: 'center',
	},
	'& .MuiDataGrid-cellCheckbox': { alignItems: 'flex-start', alignContent: 'flex-start' },
	'& .MuiDataGrid-cellCheckbox .MuiCheckbox-root': { padding: theme.spacing( 0.5 ) },
	[ `& .MuiDataGrid-cell[data-field="${GRID_DETAIL_PANEL_TOGGLE_FIELD}"]` ]: { alignContent: 'flex-start' },
	[ `& .${CANCELLED_ROW_CLASS}` ]: { textDecoration: 'line-through', opacity: 0.6 },
} ) )

type RequestedItemsTableProps = {
	items: LineItem[],
	// Nullable since sc-571048: a stock request may have no fulfilling inventory
	// until one is chosen.
	fulfillingInventory?: { name: string, isWarehouse: boolean } | null,
	showFulfillingInventoryStock: boolean,
	selectedLineItemIds: string[],
	selectionVersion: number,
	isBulkActionLoading: boolean,
	isTransferFlagLoading: boolean,
  canUserEditRequests: boolean,
	onSelectLineItem: ( lineItemId: string, isSelected: boolean ) => void,
	onCreatePurchaseOrder: () => void,
	onCreateTransfer: () => void,
	onCreateFulfillmentOrder: () => void,
	canUserCreateFulfillmentOrder: boolean,
	enableDemandSourcing: boolean,
	onBulkCompleteLineItems: () => void,
	onBulkReopenLineItems: () => void,
	onBulkCancelLineItems: () => void,
	onCompleteLineItem: ( lineItemId: string ) => void,
	onReopenLineItem: ( lineItemId: string ) => void,
	onCancelLineItem: ( lineItemId: string ) => void,
	onEditFulfillerNote: ( lineItemId: string ) => void,
	onCopy: ( text: string, label: string ) => void,
}

/**
 * Component for displaying table of requested line items
 */
export default function RequestedItemsTable( {
	items,
	fulfillingInventory,
	showFulfillingInventoryStock,
	selectedLineItemIds,
	selectionVersion,
	isBulkActionLoading,
	isTransferFlagLoading,
	canUserEditRequests,
	onSelectLineItem,
	onCreatePurchaseOrder,
	onCreateTransfer,
	onCreateFulfillmentOrder,
	canUserCreateFulfillmentOrder,
	enableDemandSourcing,
	onBulkCompleteLineItems,
	onBulkReopenLineItems,
	onBulkCancelLineItems,
	onCompleteLineItem,
	onReopenLineItem,
	onCancelLineItem,
	onEditFulfillerNote,
	onCopy,
}: RequestedItemsTableProps ) {
	const [ sortModel, setSortModel ] = useState<GridSortModel>( DEFAULT_SORT )
	const [ expandedRowIds, setExpandedRowIds ] = useState<Set<GridRowId>>( new Set() )
	const [ cellSelectionModel, setCellSelectionModel ] = useState<GridCellSelectionModel>( {} )

	// Keep the same object when nothing is highlighted, so stray clicks don't re-render the table.
	const clearCellSelection = () => setCellSelectionModel( ( prev ) => ( Object.keys( prev ).length ? {} : prev ) )

	const handleCellSelectionModelChange = ( model: GridCellSelectionModel ) => {
		const next: GridCellSelectionModel = {}
		Object.entries( model ).forEach( ( [ rowId, fields ] ) => {
			const copyable = Object.fromEntries(
				[ ...COPYABLE_FIELDS ].filter( ( field ) => fields[ field ] ).map( ( field ) => [ field, true ] ),
			)
			if ( Object.keys( copyable ).length > 0 ) {
				next[ rowId ] = copyable
			}
		} )
		setCellSelectionModel( next )
	}

	// Preferred Vendor needs nulls-last semantics in both directions, which neither
	// EUI's nor MUI's built-in comparator can express (MUI negates the comparator
	// result for descending). Sort here and let the grid render in order — the
	// column's constant sortComparator keeps MUI's stable sort from reshuffling.
	const sortedItems = useMemo( () => {
		const [ sort ] = sortModel
		if ( sort?.field !== PREFERRED_VENDOR_SORT_FIELD ) {
			return items
		}
		return [ ...items ].sort( ( a, b ) => comparePreferredVendor( a, b, sort.sort === 'desc' ? 'desc' : 'asc' ) )
	}, [ items, sortModel ] )

	// Memoized: MUI re-measures every detail panel when this identity changes.
	const getDetailPanelContent = useCallback(
		( params: { row: LineItem } ) => (
			<SourcingArtifactTable lineItem={params.row} canCancel={canUserCreateFulfillmentOrder} />
		),
		[ canUserCreateFulfillmentOrder ],
	)

	const toggleRow = ( lineItemId: GridRowId ) => {
		setExpandedRowIds( ( prev ) => {
			const next = new Set( prev )
			if ( next.has( lineItemId ) ) {
				next.delete( lineItemId )
			} else {
				next.add( lineItemId )
			}
			return next
		} )
	}

	// MUI supplies its own detail-panel toggle, but declaring the column keeps the
	// existing "Expand/Collapse sourcing details" labels the specs and screen
	// readers rely on.
	const expanderColumn: GridColDef<LineItem> = {
		field: GRID_DETAIL_PANEL_TOGGLE_FIELD,
		headerName: '',
		width: 36,
		sortable: false,
		renderCell: ( { row } ) => {
			const isExpanded = expandedRowIds.has( row.lineItemId )
			return (
				<IconButton
					size="small"
					onClick={() => toggleRow( row.lineItemId )}
					aria-label={isExpanded ? 'Collapse sourcing details' : 'Expand sourcing details'}
				>
					{isExpanded ? <ArrowDownIcon fontSize="inherit" /> : <ArrowRightIcon fontSize="inherit" />}
				</IconButton>
			)
		},
	}

	const columns = [
		...( enableDemandSourcing ? [ expanderColumn ] : [] ),
		...getRequestedItemsColumns( {
			onCompleteLineItem,
			onReopenLineItem,
			onCancelLineItem,
			onEditFulfillerNote,
			onCopy,
			fulfillingInventory,
			showFulfillingInventoryStock,
			enableDemandSourcing,
			canUserEditRequests,
		} ),
	].map( ( column ) => (
		// Ctrl+C on ticked rows copies whole rows and only respects disableExport.
		COPYABLE_FIELDS.has( column.field ) ? column : { ...column, disableExport: true }
	) )

	const selectedItems = items.filter( ( item ) => selectedLineItemIds.includes( item.lineItemId ) )

	// Check if any selected items are completed, cancelled, or pending
	const hasCompletedItems = selectedItems.some( ( item ) => item.status === 'COMPLETED' )
	const hasCancelledItems = selectedItems.some( ( item ) => item.status === 'CANCELLED' )
	const hasPendingItems = selectedItems.some( ( item ) => item.status === 'PENDING' )
	// Item-master readiness is no longer a gate on opening the action — a fulfiller
	// may need to open the order to substitute in a part that does have a master.
	// The Create Fulfillment Order modal flags and blocks any item still missing one.
	const selectionHasInFlightFO = selectedItems.length > 0 &&
		selectedItems.some( ( item ) => item.inFlightFulfillmentOrderCount > 0 )

	const rowSelectionModel: GridRowSelectionModel = {
		type: 'include',
		ids: new Set<GridRowId>( selectedLineItemIds ),
	}

	const handleRowSelectionModelChange = ( model: GridRowSelectionModel ) => {
		// MUI models selection two ways: `include` lists the selected ids, while
		// `exclude` (what the select-all header checkbox emits) lists the *un*selected
		// ones. Flatten to the concrete set before diffing.
		const newSelectedIds = model.type === 'include'
			? model.ids
			: new Set<GridRowId>(
				sortedItems
					.filter( ( item ) => !model.ids.has( item.lineItemId ) )
					.map( ( item ) => item.lineItemId ),
			)
		const currentSelectedIds = new Set( selectedLineItemIds )

		// Process additions in display order so downstream consumers see the same
		// ordering the table shows.
		sortedItems.forEach( ( item ) => {
			if ( newSelectedIds.has( item.lineItemId ) && !currentSelectedIds.has( item.lineItemId ) ) {
				onSelectLineItem( item.lineItemId, true )
			}
		} )

		// Process removals
		selectedLineItemIds.forEach( ( id ) => {
			if ( !newSelectedIds.has( id ) ) {
				onSelectLineItem( id, false )
			}
		} )
	}

	const allExpanded = items.length > 0 && items.every( ( item ) => expandedRowIds.has( item.lineItemId ) )

	const handleToggleAll = () => {
		setExpandedRowIds( allExpanded ? new Set() : new Set<GridRowId>( items.map( ( item ) => item.lineItemId ) ) )
	}

	return (
		<Paper variant="outlined" sx={{ p: 2 }}>
			<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
				<Typography variant="h6" component="h2">Requested Items ({items.length} items)</Typography>
				<Stack direction="row" alignItems="center" spacing={1}>
					{enableDemandSourcing && (
						<Button variant="text" size="small" onClick={handleToggleAll}>
							{allExpanded ? 'Collapse all' : 'Expand all'}
						</Button>
					)}
					<BulkActionsDropdown
						selectedCount={selectedLineItemIds.length}
						isLoading={isBulkActionLoading}
						hasCompletedItems={hasCompletedItems}
						hasCancelledItems={hasCancelledItems}
						hasPendingItems={hasPendingItems}
						onCreatePurchaseOrder={onCreatePurchaseOrder}
						onCreateTransfer={onCreateTransfer}
						isTransferFlagLoading={isTransferFlagLoading}
						onCreateFulfillmentOrder={onCreateFulfillmentOrder}
						canUserCreateFulfillmentOrder={canUserCreateFulfillmentOrder}
						selectionHasInFlightFO={selectionHasInFlightFO}
						onBulkCompleteLineItems={onBulkCompleteLineItems}
						onBulkReopenLineItems={onBulkReopenLineItems}
						onBulkCancelLineItems={onBulkCancelLineItems}
						canUserEditRequests={canUserEditRequests}
					/>
				</Stack>
			</Stack>

			{/*
			 * Fixed height rather than the wrapper's default autoHeight: a pinned
			 * header is only possible with autoHeight off, and this table carried a
			 * sticky header before the migration.
			 */}
			<ClickAwayListener onClickAway={clearCellSelection}>
				<Box
					sx={{ height: REQUESTS_TABLE_HEIGHT, mt: 2 }}
					onMouseDown={( event ) => {
						if ( !( event.target as HTMLElement ).closest( '.MuiDataGrid-cell' ) ) {
							clearCellSelection()
						}
					}}
				>
					<RequestsDataGrid
						key={`table-v${selectionVersion}`}
						columns={columns}
						cellSelection
						cellSelectionModel={cellSelectionModel}
						onCellSelectionModelChange={handleCellSelectionModelChange}
						rows={sortedItems}
						getRowId={( row ) => row.lineItemId}
						autoHeight={false}
						getRowHeight={() => 'auto'}
						disableColumnSorting={false}
						sortingOrder={[ 'asc', 'desc' ]}
						sortModel={sortModel}
						onSortModelChange={setSortModel}
						checkboxSelection={canUserEditRequests}
						rowSelectionModel={rowSelectionModel}
						onRowSelectionModelChange={handleRowSelectionModelChange}
						getDetailPanelContent={enableDemandSourcing ? getDetailPanelContent : undefined}
						getDetailPanelHeight={() => 'auto'}
						detailPanelExpandedRowIds={expandedRowIds}
						onDetailPanelExpandedRowIdsChange={setExpandedRowIds}
						getRowClassName={( { row } ) => (
							row.status === DemandRequestLineItemStatus.Cancelled ? CANCELLED_ROW_CLASS : ''
						)}
						/*
						 * The column floors are tuned to fit a ~1340px grid, but this table
						 * carries 16 data columns — below roughly a 1400px window it still
						 * has to scroll. Pinning the two trailing icon columns means the
						 * cancelled indicator and the actions menu stay put when it does,
						 * rather than being the first things to disappear off the edge.
						 */
						initialState={{ pinnedColumns: { right: [ 'status', 'actions' ] } }}
						columnHeaderHeight={70}
					/>
				</Box>
			</ClickAwayListener>
		</Paper>
	)
}
